/* global React, Icon */
const { useState: useStatePr, useEffect: useEffectPr } = React;

// Price tables live in src/data/pricing.js — a plain data module that both this
// component and scripts/build-jsonld.js read, so the rendered card and the
// emitted SoftwareApplication.offers can never disagree.
//
// Read lazily, never destructured at module scope. Vite splits components
// across chunks and does NOT guarantee that a chunk's module body runs after
// the entry's `import "./data/pricing.js"` side effect — in practice Pricing
// lands in a different chunk that executes first, so a top-level destructure
// throws "Cannot destructure property 'CURRENCIES' of undefined" and the whole
// page fails to mount. Same convention as Hero.jsx (window.HEALBOT_HERO_METRICS)
// and CtaBlock.jsx (window.HEALBOT_BOT_LINKS): touch `window` inside the render
// path, where the entry chain has certainly finished.
const pricingData = () => window.HEALBOT_PRICING;

function Pricing() {
  const initialLocale =
    (window.i18n && typeof window.i18n.getLocale === "function" && window.i18n.getLocale()) || "uk";
  const [locale, setLocaleState] = useStatePr(initialLocale);
  const [cur, setCur] = useStatePr(pricingData().defaultCurrencyForLocale(initialLocale));

  // Re-derive defaults when the user switches locale via Nav (Phase 3.2).
  // We reset to the locale's default currency on every i18n:change so PL
  // users see zł, RO users see lei, EN users see €, etc. Manual toggle is
  // preserved within a single locale session.
  useEffectPr(() => {
    const onChange = (e) => {
      const next =
        (e && e.detail && e.detail.locale) ||
        (window.i18n && typeof window.i18n.getLocale === "function" && window.i18n.getLocale());
      if (!next) return;
      setLocaleState(next);
      setCur(pricingData().defaultCurrencyForLocale(next));
    };
    window.addEventListener("i18n:change", onChange);
    return () => window.removeEventListener("i18n:change", onChange);
  }, []);

  const { CURRENCIES, FALLBACK_CURRENCY, toggleOrderForLocale } = pricingData();
  const c = CURRENCIES[cur] || CURRENCIES[FALLBACK_CURRENCY];
  const numLoc = window.i18n.numberLocale(locale);
  const showVatNote = ["en", "pl", "ro"].indexOf(locale) !== -1;
  const order = toggleOrderForLocale(locale);

  const tiers = () => [
    {
      tier: window.i18n.t("pricing.tier.starter.title"),
      key: "starter",
      desc: window.i18n.t("pricing.tier.starter.desc"),
      feats: [
        window.i18n.t("pricing.tier.starter.feat.1"),
        window.i18n.t("pricing.tier.starter.feat.2"),
        window.i18n.t("pricing.tier.starter.feat.3"),
        window.i18n.t("pricing.tier.starter.feat.4"),
        window.i18n.t("pricing.tier.starter.feat.5"),
        window.i18n.t("pricing.tier.starter.feat.6"),
      ],
      muted: [
        window.i18n.t("pricing.tier.starter.muted.1"),
        window.i18n.t("pricing.tier.starter.muted.2"),
      ],
      cta: window.i18n.t("pricing.tier.starter.cta"),
      variant: "ghost",
    },
    {
      tier: window.i18n.t("pricing.tier.pro.title"),
      key: "pro",
      featured: true,
      desc: window.i18n.t("pricing.tier.pro.desc"),
      feats: [
        window.i18n.t("pricing.tier.pro.feat.1"),
        window.i18n.t("pricing.tier.pro.feat.2"),
        window.i18n.t("pricing.tier.pro.feat.3"),
        window.i18n.t("pricing.tier.pro.feat.4"),
        window.i18n.t("pricing.tier.pro.feat.5"),
        window.i18n.t("pricing.tier.pro.feat.6"),
        window.i18n.t("pricing.tier.pro.feat.7"),
      ],
      muted: [],
      cta: window.i18n.t("pricing.tier.pro.cta"),
      variant: "primary",
    },
    {
      tier: window.i18n.t("pricing.tier.scale.title"),
      key: "scale",
      desc: window.i18n.t("pricing.tier.scale.desc"),
      feats: [
        window.i18n.t("pricing.tier.scale.feat.1"),
        window.i18n.t("pricing.tier.scale.feat.2"),
        window.i18n.t("pricing.tier.scale.feat.3"),
        window.i18n.t("pricing.tier.scale.feat.4"),
        window.i18n.t("pricing.tier.scale.feat.5"),
        window.i18n.t("pricing.tier.scale.feat.6"),
        window.i18n.t("pricing.tier.scale.feat.7"),
      ],
      muted: [],
      cta: window.i18n.t("pricing.tier.scale.cta"),
      variant: "dark",
    },
  ];

  return (
    <section className="section" id="pricing">
      <div className="container">
        <div className="pricing-head">
          <div>
            <span className="eyebrow">{window.i18n.t("pricing.eyebrow")}</span>
            <h2 style={{ marginTop: 16 }}>{window.i18n.t("pricing.h2")}</h2>
          </div>
          <div
            className="currency-toggle"
            role="tablist"
            aria-label={window.i18n.t("pricing.aria.currency")}
          >
            {order.map((k) => (
              <button
                key={k}
                role="tab"
                aria-selected={cur === k}
                aria-controls="pricing-grid"
                tabIndex={cur === k ? 0 : -1}
                className={cur === k ? "active" : ""}
                onClick={() => setCur(k)}
              >
                {k}
              </button>
            ))}
          </div>
        </div>
        <div className="pricing-grid" id="pricing-grid" role="tabpanel">
          {tiers().map((tr) => (
            <div key={tr.key} className={`price-card ${tr.featured ? "featured" : ""}`}>
              <div className="price-tier">{tr.tier}</div>
              <div>
                <div className="price-val">
                  <span className="cur">{c.sign}</span>
                  {c.rates[tr.key].toLocaleString(numLoc)}
                  <span className="per">{window.i18n.t("pricing.per_month")}</span>
                </div>
              </div>
              <p className="price-desc">{tr.desc}</p>
              <ul className="price-features">
                {tr.feats.map((f, i) => (
                  <li key={i}>
                    <Icon name="check" size={18} stroke={2} />
                    {f}
                  </li>
                ))}
                {tr.muted.map((f, i) => (
                  <li key={"m" + i} className="muted">
                    <Icon name="x" size={18} stroke={2} />
                    {f}
                  </li>
                ))}
              </ul>
              {tr.key === "scale" ? (
                /* Scale = enterprise sales; stays as contact CTA per plan §"Out of scope" */
                <a
                  href="#cta"
                  className={`btn ${tr.variant === "dark" ? "btn-dark" : "btn-ghost"}`}
                >
                  {tr.cta}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    window.CheckoutModal && typeof window.CheckoutModal.open === "function"
                      ? window.CheckoutModal.open({ tier: tr.key })
                      : (window.location.hash = "#cta")
                  }
                  className={`btn ${tr.variant === "primary" ? "btn-primary" : "btn-ghost"}`}
                  style={{ cursor: "pointer" }}
                >
                  {tr.cta}
                </button>
              )}
            </div>
          ))}
        </div>
        {showVatNote ? (
          <p
            className="pricing-vat-note"
            role="note"
            style={{ marginTop: 12, fontSize: 13, color: "#5a554e", lineHeight: 1.5 }}
          >
            {window.i18n.t("pricing.vat.note")}
          </p>
        ) : null}
        <div className="ppu-note">
          <div className="ppu-l">
            <div className="ppu-ic">
              <Icon name="zap" size={20} />
            </div>
            <div>
              <div className="ppu-t">{window.i18n.t("pricing.ppu.title")}</div>
              <div className="ppu-s">
                {window.i18n.t("pricing.ppu.subtitle.a")}
                {c.sign}
                {c.ppu}
                {window.i18n.t("pricing.ppu.subtitle.b")}
              </div>
            </div>
          </div>
          <a href="#cta" className="link-arrow">
            {window.i18n.t("pricing.link.more")} <Icon name="arrow" size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}

window.Pricing = Pricing;
